import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import type { TouchpointInterface } from './types';

const touchpointSDKUrl = 'https://touchpoint-sdk.alida.com/main.js';

declare global {
  interface Window {
    Touchpoint?: TouchpointInterface;
  }
}

export const useTouchpoint = (skip: boolean) => {
  const [loaded, setLoaded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const touchpointConfig = useSelector(select.cms.getTouchpointConfig);
  const { app_id: appId, pod, distribution_id: distributionId } = touchpointConfig ?? {};
  const locale = useSelector(select.locale.get);

  useEffect(() => {
    if (skip) return;

    if (window.Touchpoint) {
      setLoaded(true);
    } else {
      const script = document.createElement('script');
      script.src = touchpointSDKUrl;
      script.type = 'text/javascript';

      script.onload = () => {
        setLoaded(true);
      };
      script.async = true;

      document.head.appendChild(script);
    }
  }, [skip]);

  useEffect(() => {
    const { Touchpoint, addEventListener, removeEventListener } = window;
    const onTouchpointLoaded = () => {
      setIsReady(true);
    };

    if (loaded && appId && pod && Touchpoint) {
      addEventListener('TouchpointLoaded', onTouchpointLoaded);

      Touchpoint.initialize({
        settings: {
          containerStyle: {
            borderRadius: '8px',
            boxShadow:
              '0px 3px 5px 0px rgba(0,0,0,0.2), 0px 1px 18px 0px rgba(0,0,0,0.12), 0px 6px 10px 0px rgba(0,0,0,0.14)'
          },
          locale
        },
        visitor: {},
        publisher: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          app_id: appId,
          pod
        }
      });
    }

    return () => {
      removeEventListener('TouchpointLoaded', onTouchpointLoaded);
    };
  }, [loaded, appId, pod, locale]);

  const show = useCallback(() => {
    if (!isReady) return;

    distributionId && window.Touchpoint?.show(distributionId);
  }, [distributionId, isReady]);

  return [isReady, show] as const;
};
